import { useEffect, useMemo, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableRow, IconButton } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { SubjectArea } from '../../interfaces/EOAInterface';
import {
  buildVisibleRows,
  canExpandGroup,
  COURSE_MAPPING_GROUPS_PAGE_SIZE,
  getChildRowValues,
  getParentRowValues,
  getRowCourseId,
  CourseMappingRowValues,
} from './courseMappingTableUtils';
import { CourseMappingGroup } from '../../interfaces/CourseMappingInterface';
import { CourseMappingPendingChanges } from '../../utils/courseMappingChanges';
import {
  collectLanguageCategoryOptions,
  CourseMappingDropdownOption,
  LANGUAGE_LEVEL_OPTIONS,
  toSubjectAreaOptions,
} from './courseMappingDropdownOptions';
import TableSkeleton from '../reusable/TableSkeleton';
import CustomPagination from '../reusable/CustomPagination';
import CourseMappingTableHead from './CourseMappingTableHead';
import CourseMappingTableEditableCell from './CourseMappingTableEditableCell';
import { CourseMappingSortState } from './courseMappingSort';
import { getVisibleColumns } from './courseMappingColumnVisibility';

import './CourseMappingTable.css';

const getRowValues = (
  row: Parameters<typeof CourseMappingTableEditableCell>[0]['row'],
  pendingChanges: CourseMappingPendingChanges
): CourseMappingRowValues =>
  row.rowKind === 'parent'
    ? getParentRowValues(row.group, pendingChanges)
    : getChildRowValues(row.group, row.child, pendingChanges);

interface CourseMappingDropdownOptions {
  subjectAreas: CourseMappingDropdownOption[];
  subjectAreaConfig: SubjectArea[];
  languageCategories: CourseMappingDropdownOption[];
  languageLevels: CourseMappingDropdownOption[];
}

interface CourseMappingColumnMenuProps {
  sort: CourseMappingSortState | null;
  hiddenColumns: Set<string>;
  onSortChange: (next: CourseMappingSortState | null) => void;
  onOpenFilter: (columnKey: string) => void;
  onHideColumn: (columnKey: string) => void;
  onShowColumn: (columnKey: string) => void;
}

interface CourseMappingTableContentProps extends CourseMappingColumnMenuProps {
  groups: CourseMappingGroup[];
  startingRowNumber: number;
  currentPage: number;
  totalGroups: number;
  pageCount: number;
  pendingChanges: CourseMappingPendingChanges;
  dropdownOptions: CourseMappingDropdownOptions;
  onPendingChangesChange: (pendingChanges: CourseMappingPendingChanges) => void;
  onPageChange: (page: number) => void;
}

const CourseMappingTableContent = ({
  groups,
  startingRowNumber,
  currentPage,
  totalGroups,
  pageCount,
  pendingChanges,
  dropdownOptions,
  sort,
  hiddenColumns,
  onSortChange,
  onOpenFilter,
  onHideColumn,
  onShowColumn,
  onPendingChangesChange,
  onPageChange,
}: CourseMappingTableContentProps) => {
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(new Set());

  const visibleColumns = useMemo(() => getVisibleColumns(hiddenColumns), [hiddenColumns]);

  useEffect(() => {
    setExpandedGroupIds(new Set());
  }, [currentPage]);

  const visibleRows = useMemo(
    () => buildVisibleRows(groups, expandedGroupIds, startingRowNumber),
    [groups, expandedGroupIds, startingRowNumber]
  );

  const toggleGroupExpanded = (groupId: string) => {
    setExpandedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  return (
    <div className="course-mapping-table-with-pagination">
      <TableContainer className="course-mapping-table-container">
        <Table size="small" className="course-mapping-table" stickyHeader>
          <CourseMappingTableHead
            sort={sort}
            hiddenColumns={hiddenColumns}
            onSortChange={onSortChange}
            onOpenFilter={onOpenFilter}
            onHideColumn={onHideColumn}
            onShowColumn={onShowColumn}
          />
          <TableBody>
            {visibleRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length + 2} className="course-mapping-body-cell">
                  <p className="center-text">No courses found.</p>
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map((row) => {
                const values = getRowValues(row, pendingChanges);
                const isParent = row.rowKind === 'parent';
                const group = row.group;
                const isExpandable = isParent && canExpandGroup(group);
                const isExpanded = expandedGroupIds.has(group.id);
                const isExpandedGroupRow = isParent ? isExpandable && isExpanded : true;
                // Collapsed group parent edits the whole variant (bulk); any other row edits only its course.
                const isCollapsedGroupParent = isParent && isExpandable && !isExpanded;
                const targetCourseIds = isCollapsedGroupParent ? group.courseIds : [getRowCourseId(row)];

                return (
                  <TableRow
                    key={isParent ? `parent-${group.id}` : `child-${row.child.id}`}
                    className={`course-mapping-body-row${isExpandedGroupRow ? ' course-mapping-expanded-group-row' : ''}`}
                  >
                    <TableCell className="course-mapping-row-number-cell">
                      <span className="course-mapping-row-number">{row.rowNumber}</span>
                    </TableCell>
                    <TableCell className="course-mapping-expand-cell course-mapping-expand-cell--no-divider">
                      {isExpandable ? (
                        <IconButton
                          size="small"
                          className="course-mapping-expand-button"
                          onClick={() => toggleGroupExpanded(group.id)}
                          aria-label={isExpanded ? 'Collapse course rows' : 'Expand course rows'}
                        >
                          {isExpanded ? (
                            <KeyboardArrowDownIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowRightIcon fontSize="small" />
                          )}
                        </IconButton>
                      ) : null}
                    </TableCell>
                    {visibleColumns.map((column, columnIndex) => (
                      <TableCell
                        key={column.key}
                        className={`course-mapping-body-cell course-mapping-data-cell${
                          columnIndex === 0 ? ' course-mapping-data-cell--first' : ''
                        }${column.cellType === 'checkbox' ? ' course-mapping-data-cell--checkbox' : ''}${
                          column.cellType === 'dropdown' ? ' course-mapping-data-cell--dropdown' : ''
                        }${column.cellType === 'editable' ? ' course-mapping-data-cell--credits' : ''}`}
                      >
                        <CourseMappingTableEditableCell
                          row={row}
                          targetCourseIds={targetCourseIds}
                          columnKey={column.key}
                          cellType={column.cellType}
                          value={values[column.key as keyof CourseMappingRowValues]}
                          pendingChanges={pendingChanges}
                          dropdownOptions={dropdownOptions}
                          showOtherValuesIndicator={isCollapsedGroupParent}
                          onPendingChangesChange={onPendingChangesChange}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {totalGroups > COURSE_MAPPING_GROUPS_PAGE_SIZE && (
        <CustomPagination
          currentPage={currentPage}
          handlePageChange={onPageChange}
          pages={pageCount}
          totalItems={totalGroups}
          pageSize={COURSE_MAPPING_GROUPS_PAGE_SIZE}
        />
      )}
    </div>
  );
};

interface CourseMappingTableProps extends CourseMappingColumnMenuProps {
  groups: CourseMappingGroup[];
  totalMatching: number;
  subjectAreas: SubjectArea[];
  isPending: boolean;
  error: unknown;
  currentPage: number;
  pendingChanges: CourseMappingPendingChanges;
  onPendingChangesChange: (pendingChanges: CourseMappingPendingChanges) => void;
  onPageChange: (page: number) => void;
}

const CourseMappingTable = ({
  groups,
  totalMatching,
  subjectAreas,
  isPending,
  error,
  currentPage,
  pendingChanges,
  sort,
  hiddenColumns,
  onSortChange,
  onOpenFilter,
  onHideColumn,
  onShowColumn,
  onPendingChangesChange,
  onPageChange,
}: CourseMappingTableProps) => {
  const dropdownOptions = useMemo<CourseMappingDropdownOptions>(
    () => ({
      subjectAreas: toSubjectAreaOptions(subjectAreas),
      subjectAreaConfig: subjectAreas,
      languageCategories: collectLanguageCategoryOptions(groups),
      languageLevels: LANGUAGE_LEVEL_OPTIONS,
    }),
    [groups, subjectAreas]
  );

  const totalGroups = totalMatching;
  const pageCount = Math.max(1, Math.ceil(totalGroups / COURSE_MAPPING_GROUPS_PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, pageCount);

  const startingRowNumber = (safeCurrentPage - 1) * COURSE_MAPPING_GROUPS_PAGE_SIZE + 1;

  if (isPending) {
    return (
      <TableContainer className="course-mapping-table-container">
        <Table size="small" className="course-mapping-table" stickyHeader>
          <CourseMappingTableHead
            sort={sort}
            hiddenColumns={hiddenColumns}
            onSortChange={onSortChange}
            onOpenFilter={onOpenFilter}
            onHideColumn={onHideColumn}
            onShowColumn={onShowColumn}
          />
          <TableBody>
            <TableSkeleton rowsNumber={8} colsNumber={getVisibleColumns(hiddenColumns).length + 2} />
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  if (error) {
    return <p className="center-text">Missing course mapping data!</p>;
  }

  return (
    <CourseMappingTableContent
      groups={groups}
      startingRowNumber={startingRowNumber}
      currentPage={safeCurrentPage}
      totalGroups={totalGroups}
      pageCount={pageCount}
      pendingChanges={pendingChanges}
      dropdownOptions={dropdownOptions}
      sort={sort}
      hiddenColumns={hiddenColumns}
      onSortChange={onSortChange}
      onOpenFilter={onOpenFilter}
      onHideColumn={onHideColumn}
      onShowColumn={onShowColumn}
      onPendingChangesChange={onPendingChangesChange}
      onPageChange={onPageChange}
    />
  );
};

export default CourseMappingTable;
